// src/components/TrustBadges.jsx
// Trust-Badges für erhöhte Conversion im Shop/Checkout

import { Shield, Award, Truck, CreditCard, Phone } from "lucide-react";

const BADGES = [
    {
        icon: Shield,
        title: "Sichere Zahlung",
        subtitle: "PayPal & Überweisung",
    },
    {
        icon: Award,
        title: "40 Jahre Erfahrung",
        subtitle: "Bewährte Qualität",
    },
    {
        icon: Truck,
        title: "Schneller Versand",
        subtitle: "DHL & Spedition",
    },
    {
        icon: Phone,
        title: "Persönliche Beratung",
        subtitle: "Direkt vom Jäger",
    },
];

/**
 * Trust-Badges Leiste
 * @param {Object} props
 * @param {"horizontal"|"vertical"|"grid"} props.layout - Layout-Variante
 * @param {"default"|"compact"|"minimal"} props.variant - Größenvariante
 * @param {string} props.className - Zusätzliche CSS-Klassen
 */
export default function TrustBadges({ layout = "horizontal", variant = "default", className = "" }) {
    
    // Horizontal (für Footer/Checkout)
    if (layout === "horizontal") {
        return (
            <div className={`flex flex-wrap items-center justify-center gap-6 sm:gap-10 ${className}`}>
                {BADGES.map((badge, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center">
                            <badge.icon className="h-5 w-5 text-brand-600" />
                        </div>
                        <div className="text-left">
                            <div className="text-xs font-bold text-slate-900 dark:text-white">{badge.title}</div>
                            <div className="text-[10px] text-slate-500">{badge.subtitle}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Grid (für Shop-Sidebar)
    if (layout === "grid") {
        return (
            <div className={`grid grid-cols-2 gap-3 ${className}`}>
                {BADGES.map((badge, i) => (
                    <div key={i} className="flex flex-col items-center text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center mb-2">
                            <badge.icon className="h-4 w-4 text-brand-600" />
                        </div>
                        <div className="text-[10px] font-bold text-slate-700 dark:text-slate-200 leading-tight">{badge.title}</div>
                    </div>
                ))}
            </div>
        );
    }

    // Vertical (für Produkt-Sidebar)
    if (layout === "vertical") {
        return (
            <div className={`space-y-3 ${className}`}>
                {BADGES.map((badge, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                        <div className="h-9 w-9 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center shrink-0">
                            <badge.icon className="h-4 w-4 text-brand-600" />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-slate-800 dark:text-slate-100">{badge.title}</div>
                            <div className="text-[10px] text-slate-500">{badge.subtitle}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Compact (einzeilig, nur Icons mit Tooltip)
    if (variant === "compact") {
        return (
            <div className={`flex items-center justify-center gap-2 ${className}`}>
                {BADGES.map((badge, i) => (
                    <div 
                        key={i} 
                        className="group relative h-9 w-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors cursor-help"
                        title={`${badge.title} - ${badge.subtitle}`}
                    >
                        <badge.icon className="h-4 w-4 text-slate-500 group-hover:text-brand-600 transition-colors" />
                    </div>
                ))}
            </div>
        );
    }

    // Default fallback
    return (
        <div className={`flex flex-wrap items-center justify-center gap-6 ${className}`}>
            {BADGES.map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <badge.icon className="h-4 w-4" />
                    <span className="text-xs font-medium">{badge.title}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * Mini Trust-Badge Leiste (für Checkout)
 */
export function TrustBadgesMini({ className = "" }) {
    return (
        <div className={`flex items-center justify-center gap-4 text-slate-400 ${className}`}>
            <div className="flex items-center gap-1.5">
                <Shield className="h-4 w-4" />
                <span className="text-[10px] font-medium">Sichere Zahlung</span>
            </div>
            <div className="h-3 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-1.5">
                <Award className="h-4 w-4" />
                <span className="text-[10px] font-medium">40 Jahre Qualität</span>
            </div>
            <div className="h-3 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-1.5">
                <Truck className="h-4 w-4" />
                <span className="text-[10px] font-medium">Schneller Versand</span>
            </div>
        </div>
    );
}
